import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Copy, ShareNetwork, EnvelopeSimple, ChatCircleText, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Refer-a-friend share card for /profile.
 *
 * One-tap share to WhatsApp, SMS, Email, native share (mobile), or copy
 * link. Live counter for invited / went Pro / earned days. Reads
 * /api/referrals/me — the backend already exposes everything we need.
 */
export default function ReferralShareCard() {
  const { lang } = useI18n();
  const [referrals, setReferrals] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setReferrals(r.data)).catch(() => {});
  }, []);

  const shareUrl = useMemo(() => {
    if (!referrals?.share_url_path) return "";
    return `${window.location.origin}${referrals.share_url_path}`;
  }, [referrals?.share_url_path]);

  const shareText = useMemo(() => {
    if (!referrals) return "";
    return lang === "da"
      ? `Kom med på TeamFit — cykling, løb og padel sammen. Brug min kode ${referrals.referral_code} og få 14 dage gratis Pro. 💪 ${shareUrl}`
      : `Join me on TeamFit — cycling, running, and padel together. Use my code ${referrals.referral_code} to get 14 days of Pro free. 💪 ${shareUrl}`;
  }, [referrals, shareUrl, lang]);

  if (!referrals) {
    return (
      <section
        className="mt-10 border border-[#FFCC00]/30 bg-[#FFCC00]/[0.04] p-6"
        data-testid="referral-card-loading"
      >
        <div className="label-overline" style={{ color: "#FFCC00" }}>
          {lang === "da" ? "Inviter venner" : "Invite friends"}
        </div>
        <div className="h-12 mt-3 bg-[#0A0A0A] animate-pulse" />
      </section>
    );
  }

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referrals.referral_code);
      toast.success(lang === "da" ? "Kode kopieret" : "Code copied");
    } catch {
      toast.error(lang === "da" ? "Kunne ikke kopiere" : "Couldn't copy");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(lang === "da" ? "Link kopieret" : "Link copied");
    } catch {
      toast.error(lang === "da" ? "Kunne ikke kopiere" : "Couldn't copy");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      // Fall back to copy
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: lang === "da" ? "Kom med på TeamFit" : "Join me on TeamFit",
        text: shareText,
        url: shareUrl,
      });
    } catch { /* user cancelled — ignore */ }
  };

  const encoded = encodeURIComponent(shareText);
  const whatsappHref = `https://wa.me/?text=${encoded}`;
  const smsHref = `sms:?&body=${encoded}`;
  const subject = encodeURIComponent(
    lang === "da" ? "Kom med på TeamFit" : "Join me on TeamFit"
  );
  const mailHref = `mailto:?subject=${subject}&body=${encoded}`;

  return (
    <section
      className="mt-10 border border-[#FFCC00]/40 bg-[#FFCC00]/[0.05] p-6"
      data-testid="referral-card"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="label-overline" style={{ color: "#FFCC00" }}>
            {lang === "da" ? "Inviter venner · få Pro gratis" : "Invite friends · earn free Pro"}
          </div>
          <h2 className="font-display text-3xl tracking-tight mt-2">
            {lang === "da"
              ? `+${referrals.reward_per_referral_days || 30} DAGE FOR HVER VEN DER OPGRADERER.`
              : `+${referrals.reward_per_referral_days || 30} DAYS FOR EVERY FRIEND WHO UPGRADES.`}
          </h2>
          <p className="text-sm text-zinc-300 mt-2 max-w-xl">
            {lang === "da"
              ? "Del din kode med ét tryk. Når en ven opretter en konto og bliver Pro, får du 30 dage gratis."
              : "Share your code with one tap. When a friend signs up and goes Pro, you get 30 days free."}
          </p>

          {/* Code + link row */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div
              className="font-mono-tag bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-[#FFCC00] tracking-widest"
              data-testid="referral-code"
            >
              {referrals.referral_code}
            </div>
            <button
              onClick={copyCode}
              data-testid="referral-copy-code"
              className="kinetic-btn border border-[#262626] hover:border-white/40 px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold"
            >
              <Copy weight="bold" size={14} /> {lang === "da" ? "Kopier kode" : "Copy code"}
            </button>
            <button
              onClick={copyLink}
              data-testid="referral-copy-link"
              className="kinetic-btn border border-[#262626] hover:border-white/40 px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold"
            >
              <Copy weight="bold" size={14} /> {lang === "da" ? "Kopier link" : "Copy link"}
            </button>
          </div>

          {/* One-tap share row */}
          <div className="mt-4">
            <div className="label-overline mb-2">
              {lang === "da" ? "Del med ét tryk" : "Share in one tap"}
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="referral-share-whatsapp"
                className="kinetic-btn bg-[#25D366] hover:bg-[#20bd5a] text-black px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold"
              >
                <WhatsappLogo weight="fill" size={14} /> WhatsApp
              </a>
              <a
                href={smsHref}
                data-testid="referral-share-sms"
                className="kinetic-btn bg-[#007AFF] hover:bg-[#0a66d0] text-white px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold"
              >
                <ChatCircleText weight="fill" size={14} /> SMS
              </a>
              <a
                href={mailHref}
                data-testid="referral-share-email"
                className="kinetic-btn bg-[#1a1a1a] hover:bg-[#262626] border border-[#262626] text-white px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold"
              >
                <EnvelopeSimple weight="fill" size={14} /> {lang === "da" ? "E-mail" : "Email"}
              </a>
              <button
                onClick={nativeShare}
                data-testid="referral-share-native"
                className="kinetic-btn bg-[#FFCC00] hover:bg-[#e6b800] text-black px-3 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold"
              >
                <ShareNetwork weight="fill" size={14} /> {lang === "da" ? "Del" : "Share"}
              </button>
            </div>
          </div>
        </div>

        {/* Live counter */}
        <div className="grid grid-cols-3 gap-3 sm:min-w-[300px] w-full sm:w-auto">
          <Mini
            label={lang === "da" ? "Inviteret" : "Invited"}
            value={referrals.total_referred}
            testid="referral-counter-invited"
          />
          <Mini
            label={lang === "da" ? "Blev Pro" : "Went Pro"}
            value={referrals.converted_to_pro}
            testid="referral-counter-converted"
          />
          <Mini
            label={lang === "da" ? "Optjente dage" : "Earned days"}
            value={referrals.earned_days}
            testid="referral-counter-earned"
          />
        </div>
      </div>

      {/* Recently invited list — only show if there are any */}
      {referrals.people && referrals.people.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[#FFCC00]/20">
          <div className="label-overline mb-3">
            {lang === "da" ? "Seneste invitationer" : "Recent invites"}
          </div>
          <div className="flex flex-wrap gap-2">
            {referrals.people.slice(0, 8).map((p) => (
              <div
                key={p.user_id}
                data-testid={`referral-person-${p.user_id}`}
                className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-xs"
              >
                {p.picture ? (
                  <img src={p.picture.startsWith("http") ? p.picture : `${process.env.REACT_APP_BACKEND_URL}${p.picture}`} alt="" className="w-5 h-5 object-cover" />
                ) : (
                  <div className="w-5 h-5 bg-[#262626]" />
                )}
                <span className="text-zinc-300">{p.name || "—"}</span>
                {p.referral_bonus_credited && (
                  <span className="ml-1 text-[#FFCC00] uppercase tracking-widest font-bold text-[9px]">
                    {lang === "da" ? "Pro" : "Pro"}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Mini({ label, value, testid }) {
  return (
    <div
      className="border border-[#262626] bg-[#141414] p-3 text-center"
      data-testid={testid}
    >
      <div className="font-display text-3xl leading-none">{value}</div>
      <div className="label-overline mt-1">{label}</div>
    </div>
  );
}
